import { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useSSE — subscribes to /api/events/stream and exposes:
 *   tickMap: { [symbol]: { price, prev_price, direction, ts } }
 *   lastLifecycle: { ts, event } | null   — most recent lifecycle event
 *   lastNewsAlert: { ts, ...payload } | null
 *   status: "connecting" | "live" | "reconnecting"
 *
 * The EventSource is intentionally created once for the component lifetime.
 */
export function useSSE() {
  const [tickMap, setTickMap] = useState({});
  const [lastEvent, setLastEvent] = useState(null);
  const [lastLifecycle, setLastLifecycle] = useState(null);
  const [lastNewsAlert, setLastNewsAlert] = useState(null);
  const [lastAdminActivity, setLastAdminActivity] = useState(null);
  const [status, setStatus] = useState("connecting");
  const esRef = useRef(null);

  useEffect(() => {
    const url = `${BACKEND_URL}/api/events/stream`;
    // SSE stream is public (no auth) — `withCredentials` would force a CORS
    // preflight that the k8s ingress responds to with `Allow-Origin: *` +
    // `Allow-Credentials: true`, which browsers reject silently → no ticks.
    // Public price ticks need no cookie, so this is correct.
    const es = new EventSource(url);
    esRef.current = es;

    const normalizeTs = (rawTs) => {
      if (typeof rawTs === "number") {
        return rawTs > 1e12 ? Math.floor(rawTs / 1000) : Math.floor(rawTs);
      }
      if (typeof rawTs === "string") {
        const parsed = Date.parse(rawTs);
        return Number.isFinite(parsed) ? Math.floor(parsed / 1000) : Math.floor(Date.now() / 1000);
      }
      return Math.floor(Date.now() / 1000);
    };

    const applyTicks = (ticks, ts) => {
      const normalizedTs = normalizeTs(ts);
      setTickMap((prev) => {
        const next = { ...prev };
        for (const t of ticks) next[t.symbol] = { ...t, ts: normalizedTs };
        return next;
      });
    };

    es.addEventListener("snapshot", (e) => {
      try {
        const msg = JSON.parse(e.data);
        const ticks = (msg.data?.instruments || []).map((i) => ({
          symbol: i.symbol, price: i.price, prev_price: i.prev_price, direction: "flat",
        }));
        applyTicks(ticks, msg.ts);
        setStatus("live");
        setLastEvent(msg);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[useSSE] failed to parse snapshot:", error);
      }
    });

    es.addEventListener("tick", (e) => {
      try {
        const msg = JSON.parse(e.data);
        applyTicks(msg.data?.ticks || [], msg.ts);
        setLastEvent(msg);
        setStatus("live");
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[useSSE] failed to parse tick:", error);
      }
    });

    es.addEventListener("lifecycle", (e) => {
      try {
        const msg = JSON.parse(e.data);
        setLastLifecycle({ ts: msg.ts, ...(msg.data?.event || {}) });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[useSSE] failed to parse lifecycle:", error);
      }
    });

    es.addEventListener("news_alert", (e) => {
      try {
        const msg = JSON.parse(e.data);
        setLastNewsAlert({ ts: msg.ts, ...(msg.data || {}) });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[useSSE] failed to parse news_alert:", error);
      }
    });

    es.addEventListener("admin_activity", (e) => {
      try {
        const msg = JSON.parse(e.data);
        setLastAdminActivity({ ts: msg.ts, ...(msg.data || {}) });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[useSSE] failed to parse admin_activity:", error);
      }
    });

    es.onerror = (event) => {
      // eslint-disable-next-line no-console
      console.warn("[useSSE] connection error, will auto-reconnect:", event);
      setStatus("reconnecting");
    };

    return () => {
      es.close();
      esRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot effect
  }, []);

  return { tickMap, lastEvent, lastLifecycle, lastNewsAlert, lastAdminActivity, status };
}
